import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const sortParam = searchParams.get('sort') || 'default';
  const statusParam = searchParams.get('status') || '';

  const category = categories.find((c) => c.slug === slug) || categories[0];

  const filtered = useMemo(() => {
    let result = [...comics];
    if (slug && slug !== 'all') {
      const catName = category.name;
      result = result.filter((c) => c.category === catName);
    }
    if (statusParam === 'completed') {
      result = result.filter((c) => c.status === '已完结');
    } else if (statusParam === 'ongoing') {
      result = result.filter((c) => c.status === '连载中');
    }
    if (sortParam === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else if (sortParam === 'newest') {
      result.sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
    }
    return result;
  }, [slug, sortParam, statusParam, category.name]);

  const sortOptions = [
    { value: 'default', label: '默认排序' },
    { value: 'rating', label: '评分最高' },
    { value: 'newest', label: '最新更新' },
  ];

  const buildUrl = (sortVal: string, statusVal: string) => {
    const params = new URLSearchParams();
    if (sortVal && sortVal !== 'default') params.set('sort', sortVal);
    if (statusVal) params.set('status', statusVal);
    const qs = params.toString();
    return `/category/${slug}${qs ? `?${qs}` : ''}`;
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name}韩国漫画 - 36漫画网`,
    description: `36漫画网${category.name}漫画分类，提供无删减下拉式韩国漫画在线阅读，海量${category.name}韩漫免费阅读。`,
    url: `http://jhequip.com/category/${slug}`,
    inLanguage: 'zh-CN',
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '分类', href: '/category/all' },
    { label: category.name },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>

      <Navbar />

      <main className="pt-16">
        <section className="bg-background-50">
          <div className="mx-auto px-4 md:px-6 pt-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </section>

        <section className="py-8 md:py-12 bg-background-50">
          <div className="mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8">
              <aside className="lg:w-56 flex-shrink-0">
                <h3 className="text-foreground-900 font-semibold text-sm mb-4">漫画分类</h3>
                <nav className="space-y-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors whitespace-nowrap ${
                        cat.slug === slug
                          ? 'bg-primary-50 text-primary-700 font-medium'
                          : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                      }`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-xs`}></i>
                      </div>
                      {cat.name}
                    </Link>
                  ))}
                </nav>

                <div className="mt-8">
                  <h3 className="text-foreground-900 font-semibold text-sm mb-4">状态筛选</h3>
                  <div className="space-y-1">
                    {[
                      { value: '', label: '全部状态' },
                      { value: 'ongoing', label: '连载中' },
                      { value: 'completed', label: '已完结' },
                    ].map((opt) => (
                      <Link
                        key={opt.value}
                        to={buildUrl(sortParam, opt.value)}
                        className={`block px-3 py-2 rounded-lg text-sm transition-colors whitespace-nowrap ${
                          statusParam === opt.value
                            ? 'bg-primary-50 text-primary-700 font-medium'
                            : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                        }`}
                      >
                        {opt.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>

              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                      {category.name}韩国漫画
                    </h1>
                    <p className="text-foreground-500 text-sm mt-1">
                      共 {filtered.length} 部作品
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {sortOptions.map((opt) => (
                      <Link
                        key={opt.value}
                        to={buildUrl(opt.value, statusParam)}
                        className={`px-4 py-2 text-sm rounded-full transition-colors whitespace-nowrap ${
                          sortParam === opt.value || (!sortParam && opt.value === 'default')
                            ? 'bg-foreground-900 text-white'
                            : 'bg-background-100 text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70'
                        }`}
                      >
                        {opt.label}
                      </Link>
                    ))}
                  </div>
                </div>

                {filtered.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <i className="ri-inbox-line text-3xl text-foreground-300"></i>
                    </div>
                    <p className="text-foreground-500">暂无符合条件的漫画</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                    {filtered.map((comic) => (
                      <Link
                        key={comic.id}
                        to={`/article/${comic.slug}`}
                        className="group cursor-pointer"
                      >
                        <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3">
                          <img
                            src={comic.coverImage}
                            alt={comic.title}
                            title={`${comic.title} - ${comic.category}韩国漫画`}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute top-2 left-2 flex gap-1.5">
                            <span className="px-2 py-0.5 bg-accent-500 text-white text-[10px] font-medium rounded-full whitespace-nowrap">
                              {comic.rating}
                            </span>
                          </div>
                          {comic.status === '已完结' && (
                            <div className="absolute bottom-0 left-0 right-0 py-1.5 bg-foreground-900/80 text-center">
                              <span className="text-white text-[10px] font-medium whitespace-nowrap">已完结</span>
                            </div>
                          )}
                        </div>
                        <h3 className="text-foreground-900 font-medium text-sm mb-1 line-clamp-1">{comic.title}</h3>
                        <div className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
                          <span>{comic.author}</span>
                          <span>·</span>
                          <span>{comic.chapters}话</span>
                          {comic.isNew && (
                            <span className="px-1.5 py-0.5 bg-accent-100 text-accent-700 rounded text-[10px] font-medium whitespace-nowrap">NEW</span>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100/70">
          <div className="mx-auto px-4 md:px-6 max-w-2xl text-center">
            <h2 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">
              没找到想要的？
            </h2>
            <p className="text-foreground-500 text-sm md:text-base mb-6">
              我们的韩漫资源库每天都有新作品加入，敬请持续关注36漫画网的最新动态
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-7 py-3 bg-foreground-900 text-white text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              关于我们 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}